import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Settings, LayoutGrid, Briefcase, HeartHandshake } from 'lucide-react';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

const FloatingSidebar = () => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const { flags } = useFeatureFlags();

  const isActive = (path) => {
    if (path === '/hr') {
      return (
        location.pathname.startsWith('/hr') ||
        location.pathname.startsWith('/attendance-registers') ||
        location.pathname.startsWith('/surveys') ||
        location.pathname.startsWith('/performance-reviews') ||
        location.pathname.startsWith('/suggestion-box')
      );
    }
    return location.pathname.startsWith(path);
  };

  const menuItems: Array<{
    path: string;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    flag: string;
    badge?: number;
  }> = [
    { path: '/hr', icon: HeartHandshake, label: 'HR', flag: 'ui.pages.HR' },
    { path: '/matrix', icon: LayoutGrid, label: 'Matrix', flag: 'ui.pages.Matrix' },
    { path: '/portfolios', icon: Briefcase, label: 'Portfolios', flag: 'ui.pages.Portfolios' },
    {
      path: '/configuration',
      icon: Settings,
      label: 'Configuration',
      flag: 'ui.pages.Configuration',
    },
  ];

  const visibleItems = menuItems.filter((item) => {
    if (item.path === '/hr') {
      return (
        flags['ui.pages.AttendanceRegister'] === true ||
        flags['ui.pages.Surveys'] === true ||
        flags['ui.pages.PerformanceReviews'] === true ||
        flags['ui.pages.SuggestionBox'] === true
      );
    }
    if (item.flag === 'ui.pages.Portfolios') {
      return flags['ui.pages.Portfolios'] === true || flags['ui.pages.Library'] === true;
    }
    return flags[item.flag] === true;
  });

  return (
    <div
      className="fixed left-0 bottom-8 z-40"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`transition-all duration-300 ease-in-out overflow-visible ${
          isHovered ? 'w-56' : 'w-16'
        }`}
      >
        <div className="py-2 space-y-2 px-2">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badge = (item.badge ?? 0) > 0 ? item.badge : null;

            return (
              <Link
                key={item.path}
                to={item.path}
                aria-label={item.label}
                className={`group relative flex origin-left items-center rounded-lg px-4 py-3 transition-all duration-200 ease-out motion-safe:hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-base ${
                  active
                    ? 'bg-brand-purple text-white shadow-lg shadow-brand-purple/25'
                    : 'border border-dark-border bg-dark-elevated text-slate-300 shadow-sm hover:border-brand-purple/40 hover:bg-dark-base hover:text-white hover:shadow-md'
                }`}
                title={!isHovered ? item.label : ''}
              >
                <span className="relative">
                  <Icon
                    className={`h-5 w-5 flex-shrink-0 transition-all duration-200 ${
                      active ? 'text-white' : 'text-slate-400 group-hover:text-brand-purple-light'
                    }`}
                  />
                  {badge != null && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </span>
                <span
                  className={`ml-3 font-medium whitespace-nowrap overflow-hidden transition-all duration-300 ${
                    isHovered ? 'opacity-100 max-w-[180px]' : 'opacity-0 max-w-0'
                  }`}
                  style={{
                    transition: isHovered
                      ? 'opacity 0.3s ease-in-out 0.1s, max-width 0.3s ease-in-out'
                      : 'opacity 0.2s ease-in-out, max-width 0.2s ease-in-out',
                  }}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FloatingSidebar;
